<template>
  <div class="h-full w-full">
    <Transition :name="transitionName" mode="out-in">
      <template v-if="selectedStep === 0">
        <StepSelector
          :key="`${selectedStep}-${selectedCard.title}-${selectedCard.description}-${isChild}`"
          :cards="selectedCard.components"
          :title="selectedCard.title"
          :description="selectedCard.description"
          :is-back-button-visible="
            isChild || (Array.isArray(config.policy) && config.policy?.length > 1) || isWelcomeAvailable
          "
          @onSelect="flowSelected"
          @back="previousFlow"
        ></StepSelector>
      </template>
      <template v-else-if="selectedStep === 1 && selectedForm && selectedForm.form && formSchema">
        <FormRenderer
          :title="selectedForm.name"
          :schema="formSchema"
          :uischema="formUischema"
          :options="selectedForm.form.options"
          :step-key="selectedForm.key"
          :key="selectedForm.key"
          class="h-full w-full"
          :is-back-button-visible="isChild"
          @back="previousFlow"
          @next="flowSelected(-1)"
        ></FormRenderer>
      </template>
      <template v-else-if="selectedConfirmStep && selectedForm">
        <FormRenderer
          :title="String(selectedConfirmStep.schema.properties?.confirmStep?.title)"
          :schema="selectedConfirmStep?.schema"
          :uischema="selectedConfirmStep?.uischema"
          :options="selectedConfirmStep.options"
          :step-key="selectedForm.key"
          class="h-full w-full"
          :is-back-button-visible="false"
          :freeze-update="true"
          :state="submission"
          @back="previousFlow"
        ></FormRenderer>
      </template>
    </Transition>
  </div>
</template>

<script setup lang="ts">
  import { ref, nextTick, computed, watch, toRaw } from 'vue';
  import { config } from 'src/utils/config';
  import { sendAnalytics } from 'src/utils/analytics';
  import { StepsConfig, StepComponent, StepKey, StepForm, StepType } from 'src/types/widget';
  import { STEP_KEYS } from 'src/constants';

  import StepSelector from './StepSelector.vue';
  import FormRenderer from './FormRenderer.vue';
  import { isOpenHours } from 'src/utils/helpers';
  import { Submission } from 'src/types/api';
  import { VerticalLayout } from '@jsonforms/core';

  const emit = defineEmits<{
    (event: 'back'): void;
  }>();
  const initialStep = ref(config.value?.initialStepKey);
  const submissionReference = computed(() => config.value?.reference);
  const submission = computed(() => {
    return config.value?.sessionData?.submission as Submission;
  });
  const sessionStepKey = ref(
    config.value?.sessionData &&
      config.value?.sessionData?.currentSession &&
      'stepKey' in config.value.sessionData.currentSession
      ? config.value?.sessionData?.currentSession?.stepKey || submission.value?.stepKey
      : submission.value?.stepKey || ''
  );
  const selectedStep = ref(0);
  const hasValidContexts = computed(() => {
    if (!config.value.steps?.contextStep) return false;
    const stepArray = Object.values(config.value.steps.contextStep);
    return stepArray.some((step) => step.options.length > 1);
  });
  const context = computed(() => {
    return config.value?.context || {};
  });
  const isJlrRoadsideAssistanceStep = computed(() => {
    return context.value && 'referrer' in context.value && context.value.referrer == '24';
  });
  const formSchema = computed(() => {
    if (!selectedForm.value?.form) return undefined;
    if (isJlrRoadsideAssistanceStep.value) {
      const requiredValues = selectedForm.value?.form.schema.required?.filter((item) => item !== 'arcId');
      const finalSchema = structuredClone(toRaw(selectedForm.value?.form.schema));
      if (finalSchema.properties?.arcId) {
        delete finalSchema.properties.arcId;
      }
      finalSchema.required = requiredValues;
      return finalSchema;
    }
    return selectedForm.value?.form?.schema;
  });
  const formUischema = computed(() => {
    if (isJlrRoadsideAssistanceStep.value) {
      const finalUischema = structuredClone(toRaw(selectedForm.value?.form?.uischema)) as VerticalLayout;
      if (finalUischema.elements) {
        finalUischema.elements = finalUischema.elements.filter(
          (element) => 'scope' in element && typeof element.scope === 'string' && !element?.scope?.includes('arcId')
        );
      }
      return finalUischema;
    }
    return selectedForm.value?.form?.uischema;
  });
  const isWelcomeAvailable = computed(() => !!config.value.steps.welcomeStep || hasValidContexts.value);
  const transitionName = ref<'slide-left' | 'slide-right'>('slide-left');
  const isChild = ref(false);
  const parentIndex = ref<number[]>([]);
  const cards = ref(removeSingleComponentSteps(config.value.steps));
  const selectedCard = ref(cards.value);
  const selectedForm = ref<StepComponent>();
  const selectedConfirmStep = ref<StepForm | undefined>(selectedForm.value?.confirmStep);
  function findValue(arr: StepComponent[], val: StepKey): StepComponent | undefined {
    for (const index in arr) {
      const obj = arr[index];
      if (obj.key === val) {
        if (val === initialStep.value) {
          nextTick(() => {
            parentIndex.value = [];
            isChild.value = false;
          });
        }
        return obj;
      }
      if (obj.child?.components) {
        const originalParentIndex = [...parentIndex.value];
        parentIndex.value.unshift(Number(index));
        const result = findValue(obj.child.components, val);
        if (result) {
          return result;
        }
        parentIndex.value = originalParentIndex;
      }
    }
    return undefined;
  }
  function goToConfiguredInitialStep(stepKey?: StepKey, isInitial?: boolean) {
    if (stepKey && Object.values(STEP_KEYS).includes(stepKey)) {
      transitionName.value = 'slide-left';
      const formOrStep = findValue(cards.value.components, stepKey);

      if (formOrStep) {
        if (formOrStep.child) {
          selectedCard.value = formOrStep.child;
          if (isInitial) {
            cards.value = formOrStep.child;
          } else {
            isChild.value = true;
          }
        } else if (formOrStep.form) {
          selectedForm.value = formOrStep;
          selectedStep.value = 1;
          config.value.selectedClaim = formOrStep.key;
          if ((!isInitial && sessionStepKey.value) || isWelcomeAvailable.value || cards.value.components?.length > 1) {
            isChild.value = cards.value.components?.length > 1 || isWelcomeAvailable.value;
          }
        }
      }
    }
  }

  function removeSingleComponentSteps(parentStep: StepsConfig) {
    const filteredComponents: StepComponent[] = parentStep.components.filter((step) => {
      if (step.form) return true;
      if (step.child?.components && step.child?.components.length > 0) {
        step.child = removeSingleComponentSteps(step.child) as StepsConfig | undefined;
        return true;
      }
      return false;
    });
    if (parentStep.components.length === 1) {
      if (filteredComponents[0].child) {
        return filteredComponents[0].child;
      }
      return parentStep;
    }
    return {
      ...parentStep,
      components: filteredComponents,
    };
  }

  {
    //onCreated
    if (cards.value.components.length === 1) {
      goToConfiguredInitialStep(cards.value.components[0].key);
    } else if (sessionStepKey.value) {
      goToConfiguredInitialStep(sessionStepKey.value);
    } else if (initialStep.value) {
      goToConfiguredInitialStep(initialStep.value, true);
    }
    // else if (selectedCard.value.components.length === 1) {
    //   const nextStep = selectedCard.value.components[0];
    //   if (nextStep) {
    //     goToConfiguredInitialStep(nextStep.key, true);
    //   }
    // }

    if (submissionReference.value) {
      selectedStep.value = 2;
      selectRelevantConfirmStep();
    }
  }

  function selectRelevantConfirmStep() {
    const selectedIntegration =
      config.value.settings ||
      config.value.integrations?.find(
        ({ name, settings }) => name === 'brokerDirect' || settings?.businessHours?.length
      )?.settings;
    const isInHours = isOpenHours(selectedIntegration?.businessHours, selectedIntegration?.timezone);
    const isRecoveryRequired =
      ((submission.value?.stepType === StepType.CLAIMS && submission.value?.vehicleRoadWorthy === false) ||
        (submission.value && 'needRecovery' in submission.value && submission.value?.needRecovery)) &&
      !sessionStepKey.value.includes('repair-form');

    if (!isRecoveryRequired && isInHours) {
      selectedConfirmStep.value =
        selectedForm.value?.recoveryNotRequiredInHoursConfirmStep || selectedForm.value?.confirmStep;
      return;
    }
    if (!isRecoveryRequired && !isInHours) {
      selectedConfirmStep.value =
        selectedForm.value?.recoveryNotRequiredOutOfHoursConfirmStep || selectedForm.value?.confirmStep;
      return;
    }
    if (isRecoveryRequired && isInHours) {
      selectedConfirmStep.value =
        selectedForm.value?.recoveryIsRequiredInHoursConfirmStep || selectedForm.value?.confirmStep;
      return;
    }
    if (isRecoveryRequired && !isInHours) {
      selectedConfirmStep.value =
        selectedForm.value?.recoveryIsRequiredOutOfHoursConfirmStep || selectedForm.value?.confirmStep;
      return;
    }
    selectedConfirmStep.value = selectedForm.value?.confirmStep;
    nextTick(() => {
      scrollTo({
        behavior: 'smooth',
        top: 0,
      });
    });
  }

  function flowSelected(index: number) {
    transitionName.value = 'slide-left';
    const child = selectedCard.value.components[index]?.child;
    isChild.value = true;
    if (index === -1) {
      if (config.value.callbackOnSubmit && config.value.callbackUrl) {
        return window.location.replace(config.value.callbackUrl);
      }
      selectRelevantConfirmStep();
      isChild.value = false;
      selectedStep.value = 2;
      return;
    }
    parentIndex.value.push(index);
    if (child) {
      if (child.components.length > 1) {
        selectedCard.value = child;
        return;
      }
      selectedForm.value = child.components[0];
      selectedStep.value++;
      return;
    }
    selectedStep.value++;
    selectedForm.value = selectedCard.value.components[index];
    config.value.selectedClaim = selectedCard.value.components[index].key;
  }

  function previousFlow() {
    transitionName.value = 'slide-right';
    if (!isChild.value) {
      emit('back');
      if (config.value?.context && Object.keys(config.value?.context)?.length) {
        config.value.context = {};
      }
    }
    if (cards.value.components.length === 1 && isWelcomeAvailable.value) {
      if (config.value?.context && Object.keys(config.value?.context)?.length) {
        config.value.context = {};
      }
      return emit('back');
    }
    if (selectedStep.value === 2) {
      selectedStep.value = 0;
      parentIndex.value.pop();
    }
    if (selectedStep.value > 0) {
      selectedStep.value--;
    }

    let child: StepsConfig | undefined = cards.value;
    parentIndex.value.pop();
    parentIndex.value.forEach((index) => {
      child = child?.components[index]?.child;
    });
    config.value.selectedClaim = undefined;
    if (child) {
      selectedCard.value = child;
      isChild.value = !!parentIndex.value.length;
      goToConfiguredInitialStep(initialStep.value, true);
      return;
    }
    selectedCard.value = cards.value;
    isChild.value = false;
    goToConfiguredInitialStep(initialStep.value, true);
  }

  watch(selectedStep, () => {
    if (selectedStep.value === 1) {
      sendAnalytics('event', 'step_change', {
        event_category: 'STEP',
        event_label: 'FORM_SELECTED',
        value: selectedForm.value?.key,
        send_to: env.GA_ID,
      });
    }
  });

  defineExpose({
    selectedStep: selectedStep,
  });
</script>
